import { Banner } from '@components';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import type { FC } from 'react';

import type Enums from '@core/types/api/Enums';
import type { LinkDispatchCustomerToCompany } from '@core/types/api/LinkDispatchCustomerToCompany/LinkDispatchCustomerToCompany';
import { hasValue } from '@core/utils/equality';
import { SlabCard } from '@web/components/SlabCard/SlabCard';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { CustomerInfo } from './CustomerInfo';
import { LinkRoundIcon } from './LinkRoundIcon';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  companyId: string | undefined;
  onSubmit?: () => void;
  alertMessage?: string;
  submitButtonText?: string;
  dispatchCustomer: {
    name: string;
    integrationType?: Enums.IntegrationType | null;
    address?: string | null;
    matchStrength?: string;
    id: string;
  };
  onSaveAsDraft?: () => void;
};
export const LinkCustomerModal: FC<Props> = ({
  isOpen,
  onClose,
  companyName,
  companyId,
  onSubmit,
  alertMessage = 'We found a potential match in Slabstack.',
  submitButtonText = 'Link',
  dispatchCustomer,
  onSaveAsDraft,
}) => {
  const theme = useTheme();
  const toast = useToast();
  const linkMutation = useSlabMutation('PUT dispatch customers link', {});

  const handleSubmit = (): void => {
    if (companyId === undefined || dispatchCustomer.id === undefined) {
      return;
    }

    const links: LinkDispatchCustomerToCompany[] = [
      {
        companyId,
        dispatchCustomerId: dispatchCustomer.id,
        isAutoLinked: false,
      },
    ];
    linkMutation.mutate(
      { args: { body: links } },
      {
        onSuccess() {
          toast.success({ message: 'Dispatch customers linked successfully' });
          onClose();
          onSubmit?.();
        },
        onError() {
          toast.error({ message: 'Failed to link dispatch customers' });
        },
      },
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      sx={{
        '& .MuiDialog-paper': {
          width: '900px',
          maxWidth: 'calc(100vw - 64px)',
        },
      }}
    >
      <DialogTitle variant='h4' sx={{ fontWeight: 500 }} id='alert-dialog-title'>
        Link Customer Record
      </DialogTitle>
      <DialogContent>
        <Box gap={2} display='flex' flexDirection='column'>
          <Banner severity='info'>{alertMessage}</Banner>

          <SlabCard
            sx={{
              backgroundColor: theme.palette.SlabGray[50],
              padding: 1.5,
              my: 0,
              gap: 1,
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <Typography variant='h5/medium'>
              Potential Slabstack Match:{' '}
              <Typography variant='h6' component='span'>
                {companyName}
              </Typography>
            </Typography>

            <LinkRoundIcon />
          </SlabCard>
          <SlabCard
            sx={{
              backgroundColor: theme.palette.SlabGray[50],
              padding: 1.5,
              my: 0,
              gap: 1,
            }}
          >
            <Typography variant='h5/medium'>
              Dispatch Record (will be linked to the customer):
            </Typography>
            {hasValue(dispatchCustomer) && (
              <CustomerInfo
                customerInfo={{
                  matchStrength: dispatchCustomer.matchStrength,
                  integrationType: dispatchCustomer.integrationType,
                  dispatchCustomerName: dispatchCustomer.name,
                  address: dispatchCustomer.address,
                }}
              />
            )}
          </SlabCard>
        </Box>
      </DialogContent>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          px: 3,
          py: 2,
        }}
      >
        <Button variant='slab-secondary' sx={{ flex: 1 }} onClick={onClose}>
          Cancel
        </Button>
        {onSaveAsDraft && (
          <Button variant='slab-secondary' sx={{ flex: 1 }} onClick={onSaveAsDraft}>
            Save as Draft
          </Button>
        )}
        <Button
          variant='slab-primary'
          sx={{ flex: 1 }}
          onClick={handleSubmit}
          disabled={linkMutation.isPending}
        >
          {linkMutation.isPending ? 'Linking...' : submitButtonText}
        </Button>
      </Box>
    </Dialog>
  );
};
